/**
 * Ask for CDL Consultants or CDL Legal permission on one case (Lloyd, 14 Oct 2026: "Is there a way to send them a
 * message that allows us to get confirmation without having them check the portal?"; 21 Oct: "Send
 * message that goes to all 3: email, text, and portal").
 *
 * Sits beside the consent chip. Staff read Lloyd's exact text in the dialog and press Send. It goes
 * by text where the client agreed to texts, by email where there is a real address, and into the
 * portal thread. A Y or N by text, or the Yes or No button in the email, is recorded as their
 * sharing consent, never as an answer to an offer. One case per press.
 *
 * Shown on a CDL Consultants or CDL Legal case, naming the partner the case came from (CDL Legal:
 * Hannah, 30 Oct 2026). Where neither text nor email can reach the client, or the client has cases
 * from both partners, the button is disabled and says why.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import ClientUpdateApi, { CdlConsentRequestStatus } from '../../api/ClientUpdateApi';

interface Props {
  Ticket_guid: string;
}

function describeChannels(channels: CdlConsentRequestStatus['channels']): string {
  if (!channels) return 'by text';
  if (channels.text && channels.email) return 'by text and email';
  if (channels.email) return 'by email';
  return 'by text';
}

const CdlConsentRequestButton: React.FC<Props> = ({ Ticket_guid }) => {
  const [status, setStatus] = useState<CdlConsentRequestStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    ClientUpdateApi.cdlConsentRequest(Ticket_guid)
      .then(setStatus)
      // An older API has no such route; the case page still renders, without the button.
      .catch(() => setStatus(null));
  }, [Ticket_guid]);

  useEffect(load, [load]);

  if (!status?.applies) return null;

  const send = async (): Promise<void> => {
    setSending(true);
    setError(null);
    try {
      await ClientUpdateApi.sendCdlConsentRequest(Ticket_guid);
      setOpen(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The permission request could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const partnerName = status.partnerName ?? 'CDL Consultants';
  const waitingSince = status.openRequestSentAt
    ? new Date(status.openRequestSentAt).toLocaleDateString('en-US')
    : null;

  return (
    <Box sx={{ mt: 1 }}>
      <Tooltip title={status.cannotSend ?? ''}>
        <span>
          <Button size="small" variant="outlined" disabled={Boolean(status.cannotSend)} onClick={() => setOpen(true)}>
            {waitingSince ? `Ask ${partnerName} permission again` : `Ask ${partnerName} permission`}
          </Button>
        </span>
      </Tooltip>
      {waitingSince && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Asked on {waitingSince}. Waiting for their answer.
        </Typography>
      )}

      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ask the client about {partnerName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Goes to the client {describeChannels(status.channels)}, and into their message thread. A
            reply of Y or N by text, or the Yes or No button in the email, records whether {partnerName}{' '}
            may be told, for all of their cases. Anything else comes to you.
          </Typography>
          {status.channels && !status.channels.text && status.noTextReason && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Not by text: {status.noTextReason}
            </Alert>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            {status.text}
          </Typography>
          {waitingSince && (
            <Alert severity="info" sx={{ mt: 2 }}>
              A request sent on {waitingSince} is still waiting. Sending again replaces it.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={send} disabled={sending}>
            {sending ? 'Sending...' : 'Send text'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CdlConsentRequestButton;
